import { Link } from "react-router-dom";
import Section from "../components/ui/Section";
import Reveal from "../components/ui/Reveal";
import LiveVerdictFeed from "../components/ui/LiveVerdictFeed";
import { CASE_STUDIES, CaseStudy } from "../data/caseStudies";

const FEATURED_CASE_STUDY_IDS = [
  "multilingual-transcription-platform",
  "fraud-observability",
  "legal-decision-support",
];

const Home = () => {
  const featuredCaseStudies = FEATURED_CASE_STUDY_IDS.map((id) =>
    CASE_STUDIES.find((cs) => cs.id === id)
  ).filter((cs): cs is CaseStudy => Boolean(cs));

  return (
    <div className="bg-bg">
      {/* HERO */}
      <section className="pt-10 pb-16 sm:pt-16 sm:pb-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.15fr,0.85fr] gap-12 lg:gap-20 items-center">
            <div>
              <p className="kicker mb-4">
                Governance-First AI for Real-World Systems
              </p>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-ink mb-5 leading-[1.05]">
                NOVTIA builds the checkpoint AI has to pass before it acts.
              </h1>

              <p className="text-sm sm:text-base text-muted max-w-xl mb-7">
                NOVTIA combines strategy, engineering, and governance with a
                reusable technology foundation to build AI systems that work
                in the real world. Our products and client solutions are
                designed for control, evidence, and accountable autonomy from
                the start.
              </p>

              <div className="flex flex-wrap gap-3 mb-10">
                <Link
                  to="/products"
                  className="inline-flex items-center bg-signal px-5 py-2 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
                >
                  Explore NOVTIA Products
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center border border-line-strong px-5 py-2 text-sm text-ink hover:border-signal hover:text-signal transition-colors"
                >
                  Start a conversation
                </Link>
              </div>

              <div className="ledger-row ledger-row--two font-mono">
                <div className="ledger-cell">
                  <div className="v">Before, not after</div>
                  <div className="l">
                    Consequential actions are checked before they execute, not
                    audited once they&apos;re done
                  </div>
                </div>
                <div className="ledger-cell">
                  <div className="v">Built around your environment</div>
                  <div className="l">
                    Products, delivery, and controls designed around your
                    existing systems, data, and operations
                  </div>
                </div>
              </div>
            </div>

            <div className="novtia-hero-panel">
              <p className="novtia-hero-label mb-4">What we build on</p>
              <p className="font-display text-2xl font-semibold novtia-hero-title mb-2 leading-tight">
                Everything runs through the same control layer.
              </p>
              <p className="text-sm leading-relaxed novtia-hero-copy mb-6">
                Our products and the systems we build for clients share one
                control layer, so the same standards hold wherever AI is put to
                work.
              </p>

              <div className="novtia-hero-stack">
                <div className="novtia-hero-row">
                  <span>Products</span>
                  <span>Client systems</span>
                </div>
                <div className="novtia-hero-arrow" aria-hidden="true" />
                <div className="novtia-hero-layer">
                  <p className="novtia-hero-layer-label mb-1.5">
                    The control layer
                  </p>
                  <p className="text-base novtia-hero-layer-text">
                    Policy · Orchestration · Identity · Evidence
                  </p>
                </div>
                <div className="novtia-hero-arrow" aria-hidden="true" />
                <div className="novtia-hero-row">
                  <span>Governed AI in production</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REGULATED ENVIRONMENTS STRIP */}
      <section className="border-t border-b border-line">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="kicker">Applied across complex and regulated environments</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted">
            <span>Financial services &amp; digital lending</span>
            <span>Utilities &amp; critical infrastructure</span>
            <span>Healthcare &amp; decision support</span>
            <span>Legal &amp; professional services</span>
            <span>Enterprise &amp; operational AI</span>
          </div>
        </div>
      </section>

      {/* SOVEREIGN CORE */}
      <section className="novtia-core-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-[0.8fr,1.2fr] gap-10 lg:gap-16 items-center">
            <div>
              <p className="novtia-core-kicker mb-4">NOVTIA Sovereign Core</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-[1.08] mb-4">
                One foundation for AI that has to work in the real world.
              </h2>
              <p className="text-sm sm:text-base leading-relaxed novtia-core-copy mb-6">
                The Sovereign Core connects policy, orchestration, reasoning,
                and evidence across NOVTIA products and client systems—so
                controlled autonomy is designed in, not added later.
              </p>
              <Link to="/about" className="novtia-core-link">
                Explore the NOVTIA foundation →
              </Link>
            </div>

            <div>
              <p className="novtia-core-kicker mb-3">How it connects</p>
              <div className="novtia-core-map" aria-label="Sovereign Core system map">
                <div className="novtia-core-node novtia-core-node-top">
                  <span>Products</span>
                  <strong>AIRG · Future capabilities</strong>
                </div>
                <div className="novtia-core-node novtia-core-node-left">
                  <span>Client systems</span>
                  <strong>Data · Tools · Operations</strong>
                </div>
                <div className="novtia-core-center">
                  <span>NOVTIA</span>
                  <strong>Sovereign<br />Core</strong>
                </div>
                <div className="novtia-core-node novtia-core-node-right">
                  <span>Controls</span>
                  <strong>Guardrails · Approvals · Audit trail</strong>
                </div>
                <div className="novtia-core-node novtia-core-node-bottom">
                  <span>Outcomes</span>
                  <strong>Governed AI in production</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF: PRODUCT + CASE STUDIES, ONE SECTION */}
      <Reveal>
        <section className="border-b border-line">
          <Section
            eyebrow="Proof in the field"
            title="The foundation, applied to real work."
            description="NOVTIA turns the same governance-first principles into products, client systems, and operational improvements that can stand up to real-world constraints."
          >
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start mb-14">
              <div className="space-y-4">
                <p className="kicker">In client environments</p>
                <p className="text-base text-ink leading-relaxed">
                  Most of our work runs inside organisations that already have
                  systems, data, and obligations in place. We design and build
                  governed AI around what is already there, rather than asking
                  the business to rebuild around the AI.
                </p>
                <ul className="border-t border-line">
                  {[
                    {
                      t: "Strategy and process redesign",
                      d: "Find where AI changes the decision, not just the interface.",
                    },
                    {
                      t: "Engineering and integration",
                      d: "Built into existing stacks, data, and operational workflows.",
                    },
                    {
                      t: "Runtime control and evidence",
                      d: "The same governance that runs in our product, applied to yours.",
                    },
                  ].map((item) => (
                    <li key={item.t} className="border-b border-line py-3.5">
                      <p className="text-sm font-semibold text-ink">{item.t}</p>
                      <p className="text-xs text-muted mt-1 leading-relaxed">
                        {item.d}
                      </p>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/services"
                  className="inline-flex text-sm font-semibold text-signal hover:underline"
                >
                  Explore services →
                </Link>
              </div>
              <div className="space-y-4">
                <p className="kicker">In our own product</p>
                <p className="text-base text-ink leading-relaxed">
                  AIRG packages the same controls as a product. Every agent
                  action is judged before it executes and sealed as evidence —
                  live today with a small number of design partners.
                </p>
                <LiveVerdictFeed />
                <Link
                  to="/products/airg"
                  className="inline-flex text-sm font-semibold text-signal hover:underline"
                >
                  Explore AIRG →
                </Link>
              </div>
            </div>

            <p className="kicker mb-4">Selected client work</p>
            <div className="grid md:grid-cols-3 gap-6">
              {featuredCaseStudies.map((cs) => (
                <div key={cs.id} className="surface-card case-proof-card p-5">
                  <p className="kicker mb-2.5">{cs.sector}</p>
                  <h3 className="font-display text-lg font-semibold text-ink mb-2 leading-snug">
                    {cs.title}
                  </h3>
                  <p className="text-xs text-muted mb-3">{cs.client}</p>
                  <p className="text-sm text-muted line-clamp-3 leading-relaxed mb-5">
                    {cs.summary}
                  </p>
                  {cs.impactMetrics[0] && (
                    <p className="case-proof-outcome">{cs.impactMetrics[0]}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-5">
              <Link
                to="/case-studies"
                className="text-sm font-semibold text-signal hover:underline"
              >
                See more case studies →
              </Link>
            </div>
          </Section>
        </section>
      </Reveal>

      {/* FINAL CTA */}
      <Reveal>
        <section>
          <Section eyebrow="Ready to talk?">
            <div className="surface-card p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h2 className="font-display text-xl sm:text-2xl font-semibold text-ink mb-2">
                  Let&apos;s find the right way to work together.
                </h2>
                <p className="text-sm text-muted max-w-xl">
                  Redesign a critical process, move an AI pilot into
                  production, or put controls in front of agents you&apos;re
                  already running — NOVTIA can help shape and deliver the right
                  approach.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 flex-none">
                <Link
                  to="/contact"
                  className="inline-flex items-center bg-signal px-6 py-2.5 text-sm font-semibold text-on-signal hover:bg-signal-strong transition-colors"
                >
                  Start a conversation
                </Link>
              </div>
            </div>
          </Section>
        </section>
      </Reveal>
    </div>
  );
};

export default Home;
